import React from 'react';
import DashboardLayout from './DashboardLayout';
import { 
  Users, 
  Settings, 
  Shield, 
  Database, 
  Server,
  Monitor,
  HardDrive,
  Activity,
  UserPlus,
  Eye
} from 'lucide-react';

export const AdminDashboard: React.FC = () => {
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Admin', href: '/dashboard/admin' }
  ];

  const kpis = [
    { id: '1', title: 'Total Users', value: '52', change: -5.5, trend: 'down' },
    { id: '2', title: 'Active Sessions', value: '15', change: -31.8, trend: 'down' },
    { id: '3', title: 'System Uptime', value: '99.8%', change: 0.3, trend: 'up' },
    { id: '4', title: 'Storage Used', value: '67%', change: -9.5, trend: 'down' }
  ];

  const recentUsers = [
    { id: '1', name: 'Dr. Sarah Johnson', email: 'sarah@reprotech.com', role: 'Veterinarian', lastLogin: '2 hours ago', status: 'active' },
    { id: '2', name: 'Mike Chen', email: 'mike@reprotech.com', role: 'Technician', lastLogin: '4 hours ago', status: 'active' },
    { id: '3', name: 'Admin User', email: 'admin@reprotech.com', role: 'Administrator', lastLogin: '1 hour ago', status: 'active' }
  ];

  return (
    <DashboardLayout 
      title="Admin Dashboard" 
      breadcrumbs={breadcrumbs}
      actions={[
        {
          label: 'Add User',
          icon: UserPlus,
          onClick: () => console.log('Add user'),
          variant: 'primary'
        },
        {
          label: 'System Logs',
          icon: Eye,
          onClick: () => console.log('View logs'),
          variant: 'secondary'
        }
      ]}
    >
      <div className="space-y-6">
        {/* KPI Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((kpi) => (
            <div key={kpi.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{kpi.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                </div>
                <div className={`text-sm ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.change > 0 ? '+' : ''}{kpi.change}%
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium">User Management</h3>
                <p className="text-sm text-gray-600">Manage system users</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Settings className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium">System Settings</h3>
                <p className="text-sm text-gray-600">Configure system</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Shield className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-medium">Security Center</h3>
                <p className="text-sm text-gray-600">Security settings</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Database className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium">Database Management</h3>
                <p className="text-sm text-gray-600">Manage database</p>
              </div>
            </div>
          </div>
        </div>

        {/* System Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">System Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <Server className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Server Status</p>
                <p className="font-semibold">Online</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <Monitor className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600">CPU Usage</p>
                <p className="font-semibold">45%</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <HardDrive className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Memory Usage</p>
                <p className="font-semibold">67%</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Processes</p>
                <p className="font-semibold">127</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Recent System Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">Admin user logged in</p>
                    <p className="text-xs text-gray-500">2 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">System updated to v2.1.0</p>
                    <p className="text-xs text-gray-500">1 hour ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium">Database backup completed</p>
                    <p className="text-xs text-gray-500">3 hours ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Recent Users</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium">{user.name}</h4>
                      <p className="text-sm text-gray-600">{user.role} • {user.lastLogin}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {user.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;

